import { translate } from '../../../helper/translate';
import React, { useEffect, useState } from 'react';
import { GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { setPageCacheHeaders } from '../../../helper/cache-control';
import { useRouter } from 'next/router';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';

import { SEO } from '../../../helper/seo';
import styles from '../../../styles/Article.module.scss';
import {
  getArticleList,
  getArticleRes,
  getCaseStudyList,
  getEditable,
  getVideoList,
  getWebinarList,
  getWhitePaperList,
} from '../../../helper';
import { ArticleRes, ResourceMetadataRes, ResourcesRes } from '../../../typescript/pages';
import {
  HeroBanner as HeroBannerProp,
  HeroTitle as HeroTitleProp,
  HeroBannerStyleButton,
} from '../../../typescript/component';
import HeroBanner from '../../../components/banners/hero-banner';
import HeroTitle from '../../../components/ui/hero-title';
import ResourceAside from '../../../components/specialized/resource-aside';
import { getRandomItems } from '../../../helper/utils';
import {
  getSimilarResourcesHybrid,
  testAlgoliaConnection,
} from '../../../helper/algoliaSimilarity';
import { HtmlBlock } from '../../../components/media/html-block';

// Live Preview imports
import ContentstackLivePreview from '@contentstack/live-preview-utils';
import { initLivePreview, initializeContentStackNewSdk } from '../../../contentstack-sdk/utils';
import { usePageState } from '../../../context/pageContext';
import moment from 'moment';

export default function ArticlePage({
  article,
  randomResources,
  locale,
  availableLocales,
  source
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [, setAvailableLocales] = usePageState('availableLocales');
  const [isPreview, setPreview] = useState(false);
  const [data, setData] = useState(article);
  const router = useRouter();
  if (router.isFallback) return <div>Loading...</div>;

  // Keep the shared page context in sync with this entry's locales, and clear it
  // on unmount so other pages don't inherit a stale list. Depending on the prop
  // matters because Next reuses this component when navigating between two
  // entries of the same type, so a mount-only effect would never re-run.
  useEffect(() => {
    setAvailableLocales(availableLocales ?? []);
    return () => setAvailableLocales([]);
  }, [availableLocales]);

  const readingTime = Math.ceil((data.content || '').split(/\s+/).length / 200);

  const formatDate = (date: string, locale?: string) => {
    if (locale) {
      moment.locale(locale);
    }

    return moment(date).format('MMMM D, YYYY');
  }

  const heroBannerData: HeroBannerProp = {
    banner_eyebrow: translate('article', locale).toUpperCase(),
    banner_title: data.title,
    banner_title_alt: '',
    display_options: {
      align_image: 'Right',
      align_text: 'Left',
      background_color: 'Light',
      breadcrumb: true,
      show_button: false,
      split_50_50: true,
    },
    header_tag_type: 'H1',
    banner_media: data.featured_image,
    banner_media_alt: data.featured_image_alt,
    button: {
      link: '',
      title: '',
      style: HeroBannerStyleButton.SOLID,
    },
    anchor: '',
    $: data.$
  };

  const heroTitleData: HeroTitleProp = {
    align_text: 'Left',
    bottom_border: true,
    content: '',
    eyebrow: translate('article', locale).toUpperCase(),
    header_tag_type: 'H1',
    show_breadcrumb: true,
    title: data.title,
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const editableFields = {
    title: getEditable(data, 'title'),
    banner_title: getEditable(data, 'title'),
    banner_media: getEditable(data, 'featured_image'),
    description: getEditable(data, 'content')
  }

  const getContent = async ({ stack }: {stack: any}) => {
    const page = await getArticleRes({
      entryUrl: window.location.pathname,
      locale: locale,
      stackInstance: stack,
      clientSide: true
    });

    // Store data into state so we can dynamically change update the fields
    setData(page);
  };

  useEffect(() => {
    setData(article);
  }, [article]);

  useEffect(() => {
    const stack = initializeContentStackNewSdk({ preview: true });

    initLivePreview({stackInstance: stack});
    ContentstackLivePreview.onEntryChange(() => {
      
      // It's loaded from the live preview so we want to finish up the setup
      if (stack.config.live_preview && stack.config.live_preview.live_preview) {
        setPreview(true);
        getContent({stack});
      }
    });
  }, []);

  const metadata = getMetaData(article);

  return (
    <>
      <SEO
        meta_title={article?.global?.meta_title}
        meta_description={article?.global?.meta_description}
        keywords={article?.global?.keywords}
        share_image={article?.featured_image?.url}
        currentLocale={locale}
        baseUrl={process.env.NEXT_PUBLIC_HOSTED_URL || ''}
        contentType="Article"
        publishDate={article?.updated_at || undefined}
        modifiedDate={article?.updated_at || undefined}
      />
      <div className={styles.outerWrapper}>
        {article.featured_image?.url ? (
          <HeroBanner key="hero" hero_banner={heroBannerData} editable={isPreview ? true : false} editableFields={editableFields} />
        ) : (
          <HeroTitle key="hero" hero_title={heroTitleData} editable={editableFields} />
        )}

        <div className={styles.moduleWrapper}>
          <div className={styles.root}>
            <section className={styles.virtualTour}>
              <div className={styles.articleHead}>
                {metadata.show_date && metadata.date && (
                  <p className={styles.articleHeadDate}>
                    <strong>Last Updated: </strong>
                    {formatDate(metadata.date, locale)}
                  </p>
                )}
                <div className={styles.readingLine}>
                  <div className={styles.line}></div>
                  {readingTime} min{readingTime > 1 ? 's' : ''}
                </div>
                <div className={styles.socialWrapper}>
                  <LinkedinShareButton url={shareUrl}>
                    <svg
                      width="21"
                      height="21"
                      viewBox="0 0 21 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        xmlns="http://www.w3.org/2000/svg"
                        id="Vector"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.115235 1.73562C0.115235 1.28877 0.292743 0.860227 0.608711 0.54426C0.924679 0.228292 1.35322 0.0507835 1.80007 0.0507835H18.5952C18.8167 0.0504218 19.036 0.0937412 19.2407 0.178261C19.4454 0.262781 19.6314 0.386842 19.7881 0.543342C19.9447 0.699841 20.069 0.885705 20.1537 1.09029C20.2385 1.29488 20.282 1.51417 20.2819 1.73562V18.5308C20.2821 18.7523 20.2387 18.9716 20.1541 19.1763C20.0695 19.381 19.9453 19.567 19.7888 19.7237C19.6322 19.8803 19.4463 20.0046 19.2416 20.0893C19.037 20.174 18.8176 20.2176 18.5962 20.2175H1.80007C1.57874 20.2175 1.35957 20.1738 1.1551 20.0891C0.950625 20.0044 0.76485 19.8802 0.608387 19.7237C0.451924 19.5671 0.327839 19.3813 0.243222 19.1767C0.158605 18.9722 0.115114 18.753 0.115235 18.5317V1.73562ZM8.09757 7.73978H10.8283V9.11112C11.2225 8.32278 12.2308 7.61328 13.7461 7.61328C16.651 7.61328 17.3394 9.18353 17.3394 12.0646V17.4015H14.3997V12.721C14.3997 11.0801 14.0055 10.1543 13.0045 10.1543C11.6157 10.1543 11.0382 11.1525 11.0382 12.721V17.4015H8.09757V7.73978ZM3.0559 17.2759H5.99657V7.61328H3.0559V17.275V17.2759ZM6.41732 4.46178C6.42286 4.71357 6.37806 4.96392 6.28554 5.19816C6.19302 5.43239 6.05464 5.64578 5.87853 5.82581C5.70242 6.00584 5.49213 6.14888 5.25999 6.24654C5.02785 6.3442 4.77854 6.3945 4.52669 6.3945C4.27485 6.3945 4.02554 6.3442 3.7934 6.24654C3.56126 6.14888 3.35097 6.00584 3.17486 5.82581C2.99875 5.64578 2.86037 5.43239 2.76785 5.19816C2.67533 4.96392 2.63052 4.71357 2.63607 4.46178C2.64695 3.96757 2.85092 3.49726 3.2043 3.15158C3.55767 2.8059 4.03236 2.61233 4.52669 2.61233C5.02103 2.61233 5.49572 2.8059 5.84909 3.15158C6.20247 3.49726 6.40644 3.96757 6.41732 4.46178Z"
                        fill="var(--primary-500)"
                      />
                    </svg>
                  </LinkedinShareButton>
                  <TwitterShareButton url={shareUrl}>
                    <svg
                      width="23"
                      height="23"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        id="Vector"
                        d="M13.2917 9.42426L21.4817 0H19.5409L12.4296 8.18295L6.74974 0H0.19873L8.78775 12.3741L0.19873 22.2569H2.13961L9.6494 13.6154L15.6477 22.2569H22.1987L13.2912 9.42426H13.2917ZM10.6334 12.4831L9.76318 11.2509L2.83893 1.44634H5.82L11.4079 9.35894L12.2782 10.5911L19.5419 20.8763H16.5608L10.6334 12.4836V12.4831Z"
                        fill="var(--primary-500)"
                      />
                    </svg>
                  </TwitterShareButton>
                  <FacebookShareButton url={shareUrl}>
                    <svg
                      width="13"
                      height="23"
                      viewBox="0 0 13 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        id="Vector"
                        d="M11.3179 12.5092L11.9291 8.52789H8.10868V5.94426C8.10868 4.85526 8.64218 3.79307 10.3534 3.79307H12.09V0.4037C12.09 0.4037 10.5142 0.134888 9.00724 0.134888C5.86124 0.134888 3.80493 2.04201 3.80493 5.49395V8.52857H0.307617V12.5099H3.80493V22.1349H8.10868V12.5099L11.3179 12.5092Z"
                        fill="var(--primary-500)"
                      />
                    </svg>
                  </FacebookShareButton>
                  <EmailShareButton url={shareUrl}>
                    <svg
                      width="19"
                      height="20"
                      viewBox="0 0 19 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        id="Vector"
                        d="M7.36541 15.2217L5.74291 16.7984C5.31746 17.2238 4.74042 17.4628 4.13874 17.4628C3.53706 17.4628 2.96003 17.2238 2.53458 16.7984C2.10913 16.3729 1.87011 15.7959 1.87011 15.1942C1.87011 14.5925 2.10913 14.0155 2.53458 13.59L6.69624 9.4192C7.10472 9.0094 7.6551 8.77229 8.23351 8.75693C8.81192 8.74158 9.3741 8.94915 9.80374 9.3367L9.91374 9.42837C10.0876 9.59855 10.3219 9.69271 10.5651 9.69013C10.8084 9.68755 11.0406 9.58845 11.2108 9.41462C11.381 9.24079 11.4752 9.00648 11.4726 8.76323C11.47 8.51998 11.3709 8.28772 11.1971 8.11753C11.1453 8.05064 11.0903 7.98639 11.0321 7.92503C10.2496 7.24424 9.23755 6.88631 8.20101 6.92376C7.16448 6.9612 6.18092 7.39122 5.44958 8.1267L1.23291 12.2975C0.516403 13.0774 0.128895 14.104 0.151312 15.1628C0.173728 16.2216 0.604335 17.2309 1.35321 17.9797C2.10208 18.7286 3.11131 19.1592 4.17014 19.1816C5.22896 19.2041 6.25552 18.8165 7.03541 18.1L8.62124 16.5509C8.77783 16.38 8.8639 16.1563 8.86215 15.9245C8.8604 15.6928 8.77096 15.4704 8.61181 15.3019C8.45267 15.1335 8.23564 15.0316 8.00439 15.0167C7.77314 15.0018 7.54483 15.0751 7.36541 15.2217ZM17.1646 2.16837C16.3935 1.40204 15.3505 0.971924 14.2633 0.971924C13.1762 0.971924 12.1332 1.40204 11.3621 2.16837L9.77624 3.71753C9.61966 3.88836 9.53359 4.11214 9.53534 4.34386C9.53709 4.57558 9.62653 4.79804 9.78568 4.96648C9.94482 5.13492 10.1618 5.23683 10.3931 5.25171C10.6243 5.2666 10.8527 5.19335 11.0321 5.0467L12.6179 3.47003C13.0434 3.04458 13.6204 2.80557 14.2221 2.80557C14.8238 2.80557 15.4008 3.04458 15.8262 3.47003C16.2517 3.89549 16.4907 4.47252 16.4907 5.0742C16.4907 5.67588 16.2517 6.25292 15.8262 6.67837L11.6646 10.8492C11.2561 11.259 10.7057 11.4961 10.1273 11.5115C9.5489 11.5268 8.98672 11.3193 8.55708 10.9317L8.44708 10.84C8.27325 10.6699 8.03894 10.5757 7.79569 10.5783C7.55244 10.5809 7.32017 10.68 7.14999 10.8538C6.97981 11.0276 6.88565 11.2619 6.88823 11.5052C6.89081 11.7484 6.98992 11.9807 7.16374 12.1509C7.23033 12.219 7.30072 12.2832 7.37458 12.3434C8.15803 13.0221 9.16962 13.3787 10.2055 13.3413C11.2414 13.3039 12.2246 12.8752 12.9571 12.1417L17.1279 7.97087C17.8991 7.2046 18.3358 6.16433 18.3427 5.07718C18.3496 3.99003 17.9261 2.94432 17.1646 2.16837Z"
                        fill="var(--primary-500)"
                      />
                    </svg>
                  </EmailShareButton>
                </div>
              </div>
              <HtmlBlock html={data.content} className={styles.description} editable={getEditable(data, 'content')} />
            </section>

            <div className={styles.divider} />
            <div className={styles.taxonomyWrapper}>
              <ResourceAside
                key={data.uid}
                relatedResources={randomResources}
                type="Article"
                source={source}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

interface Props {
  article: ArticleRes;
  randomResources: ResourcesRes[];
  locale: string;
  source: 'algolia' | 'random' | 'mixed';
  preview?: boolean;
}

const cleanMetadata = (metadata = {}) =>
  Object.fromEntries(
    Object.entries(metadata)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
  );

const defaultMetadata = {
  author: '',
  show_author: true,
  show_date: true,
  date: ""
}

const getMetaData = (cs: ArticleRes) => {
  const currentMetadata = cleanMetadata(cs?.metadata_tags);
  // const enMetadata = cleanMetadata(
  //   enResources?.[cs?.uid]?.metadata_tags
  // );

  const finalMetadata: ResourceMetadataRes = {
    ...defaultMetadata,
    // ...enMetadata,
    ...currentMetadata
  }

  // Use create date if date is empty on all three levels
  // if (!finalMetadata.date) {
  //   finalMetadata.date = cs?.created_at ?? '';
  // }

  // We should force showing date as long as it's enabled in en-us
  // if (enMetadata.show_date === true)
  //   finalMetadata.show_date = true;

  return finalMetadata;
}

// SSG: Get article data by ID
export const getServerSideProps: GetServerSideProps<{
  article: ArticleRes;
  randomResources: ResourcesRes[];
  locale: string;
  availableLocales: string[];
  source: 'algolia' | 'random' | 'mixed';
}> = async ({ params, locale, locales, res }) => {
  const availableLocales = [];
  const lang = locale || 'en-us';
  const articleId = params?.articleId as string;

  const article = await getArticleRes({
    entryUrl: `/resources/article/${articleId}`,
    locale: lang
  });
  if (!article) return { notFound: true };

  if (locales && locales.length) {
    for (let l of locales) {
      const localeResource = await getArticleRes({
        entryUrl: `/resources/article/${articleId}`,
        locale: l
      });

      if (localeResource) {
        availableLocales.push(l);
      }
    }
  }

  // Try to get similar resources from Algolia first
  let similarResources: ResourcesRes[] = [];
  let source: 'algolia' | 'random' | 'mixed' = 'random';

  try {
    const result = await getSimilarResourcesHybrid(
      {
        uid: article.uid,
        title: article.title,
        description: article.description || article.summary, // Use description (matches Algolia field)
        keywords: article.global?.keywords
          ? article.global.keywords.split(',').map(k => k.trim())
          : [],
        content: article.content,
        type: 'Article',
      },
      {
        locale: lang,
        maxResults: 3,
        excludeCurrentId: article.url, // Use URL as objectID
      },
    );

    similarResources = result.resources;
    source = result.source;
  } catch (error) {
    // Silently fail and use fallback
  }

  // Fallback to random resources if Algolia similarity fails or returns no results
  if (similarResources.length === 0) {
    // Get random resources from all types, not just articles
    const [articles, caseStudies, videos, webinars, whitepapers] = await Promise.all([
      getArticleList(lang),
      getCaseStudyList(lang),
      getVideoList(lang),
      getWebinarList(lang),
      getWhitePaperList(lang),
    ]);

    // Flatten and combine all resource types
    const allResources = [
      ...articles.flat(),
      ...caseStudies,
      ...videos,
      ...webinars,
      ...whitepapers,
    ];

    similarResources = getRandomItems(allResources, 3);
    source = 'random';
  }

  setPageCacheHeaders(res);

  return {
    props: { article, randomResources: similarResources, locale: lang, availableLocales, source },
  };
};

// SSR: Get article data by ID
// export const getServerSideProps: GetServerSideProps<Props> = async (
//   context: GetServerSidePropsContext
// ) => {
//   const { params, locale, query } = context;
//   const lang = locale || 'en-us';
//   const articleId = params?.articleId as string;

//   // Detect live_preview from query string (from Contentstack iframe)
//   const livePreviewParam = query?.live_preview as string | undefined;
//   var isPreview = !!livePreviewParam; // true if live_preview exists

//   const previewParams = {
//     live_preview: query.live_preview as string,
//     content_type_uid: query.content_type_uid as string,
//     entry_uid: query.entry_uid as string,
//     articleId: query.articleId as string
//   }

//   const article = await getArticleRes({
//     entryUrl: `/resources/article/${articleId}`,
//     locale: lang,
//     preview: isPreview ? (typeof query.builder !== "undefined" && query.builder == "true" ? "builder" : true) : false,
//     previewParams: previewParams
//   });
//   if (!article) return { notFound: true };

//   // Try to get similar resources from Algolia first
//   let similarResources: ResourcesRes[] = [];
//   let source: 'algolia' | 'random' | 'mixed' = 'random';

//   try {
//     const result = await getSimilarResourcesHybrid(
//       {
//         uid: article.uid,
//         title: article.title,
//         description: article.description || article.summary, // Use description (matches Algolia field)
//         keywords: article.global?.keywords
//           ? article.global.keywords.split(',').map(k => k.trim())
//           : [],
//         content: article.content,
//         type: 'Article',
//       },
//       {
//         locale: lang,
//         maxResults: 3,
//         excludeCurrentId: article.url, // Use URL as objectID
//       },
//     );

//     similarResources = result.resources;
//     source = result.source;
//   } catch (error) {
//     // Silently fail and use fallback
//   }

//   // Fallback to random resources if Algolia similarity fails or returns no results
//   if (similarResources.length === 0) {
//     // Get random resources from all types, not just articles
//     const [articles, caseStudies, videos, webinars, whitepapers] = await Promise.all([
//       getArticleList(lang),
//       getCaseStudyList(lang),
//       getVideoList(lang),
//       getWebinarList(lang),
//       getWhitePaperList(lang),
//     ]);

//     // Flatten and combine all resource types
//     const allResources = [
//       ...articles.flat(),
//       ...caseStudies,
//       ...videos,
//       ...webinars,
//       ...whitepapers,
//     ];

//     similarResources = getRandomItems(allResources, 3);
//     source = 'random';
//   }

//   return {
//     props: {
//       article,
//       randomResources: similarResources,
//       locale: lang,
//       source,
//       preview: isPreview
//     },
//   };
// };
